<template lang="html">
  <div id="distributors-page">
    <section id="distribuidores" class="bg-light">
      <!-- Hero -->
      <div class="distributors-hero d-flex align-items-center text-center">
        <div class="container">
          <h1 class="distributors-hero-title mb-3">Distribuidores</h1>
          <p class="distributors-hero-subtitle mb-0 mx-auto">
            Red nacional de socios especializados en tecnología de drones para brindar soporte local y profesional
          </p>
        </div>
      </div>

      <div class="container py-5">
        <!-- Directorio -->
        <div class="mb-5">
          <div class="text-center mb-4">
            <h2 class="h2 font-weight-bold mb-3">Directorio de Distribuidores</h2>
            <p class="lead text-muted mb-0 mx-auto distributors-intro">
              Encuentra a nuestros distribuidores autorizados en todo México para obtener soporte local y especializado en
              soluciones de drones.
            </p>
          </div>

          <!-- Búsqueda -->
          <div class="row justify-content-center mb-4">
            <div class="col-12 col-md-10 col-lg-7">
              <b-input-group>
                <b-input-group-prepend is-text>
                  <i class="fa-solid fa-magnifying-glass text-muted"></i>
                </b-input-group-prepend>
                <b-form-input
                  v-model.trim="searchTerm"
                  type="text"
                  placeholder="Buscar por nombre o ubicación..."
                  aria-label="Buscar por nombre o ubicación"
                />
              </b-input-group>
            </div>
          </div>

          <!-- Cards -->
          <div class="row">
            <div
              v-for="d in filteredDistributors"
              :key="d.id"
              class="col-12 col-lg-6 mb-4"
            >
              <div class="card h-100 border-0 shadow-sm distributor-card">
                <div class="card-body">
                  <div class="text-center mb-3">
                    <img
                      :src="d.image || placeholderLogo"
                      :alt="d.name"
                      class="img-fluid distributor-logo"
                    >
                  </div>

                  <h5 class="font-weight-bold mb-2 text-center">{{ d.name }}</h5>
                  <p class="text-muted mb-3 text-center">{{ d.description }}</p>

                  <div class="distributor-meta">
                    <div class="d-flex align-items-start mb-2" v-if="d.address">
                      <i class="fa-solid fa-location-dot text-danger mr-2 mt-1"></i>
                      <span class="text-muted small">{{ d.address }}</span>
                    </div>

                    <div class="d-flex align-items-start mb-2" v-if="d.phone">
                      <i class="fa-solid fa-phone text-danger mr-2 mt-1"></i>
                      <!-- <a class="text-muted small" href="">+52 55 9876 5432</a> -->
                       <span v-html="onlyNumbers(d.phone)"></span>
                    </div>

                    <div class="d-flex align-items-start mb-2" v-if="d.email">
                      <i class="fa-solid fa-envelope text-danger mr-2 mt-1"></i>
                      <a class="text-muted small" :href="'mailto:' + d.email">{{ d.email }}</a>
                    </div>

                    <div class="d-flex align-items-start" v-if="d.website">
                      <i class="fa-solid fa-globe text-danger mr-2 mt-1"></i>
                      <a class="text-muted small" :href="normalizeWebsite(d.website)" target="_blank" rel="noopener">
                        {{ d.website }}
                      </a>
                    </div>
                  </div>

                  <div class="pt-3" v-if="d.phone">
                    <a class="btn btn-danger btn-block" :href="phoneNumber(d.phone)">
                      <i class="fa-solid fa-phone"></i> Contactar Distribuidor
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="filteredDistributors.length === 0" class="col-12">
              <div class="alert alert-secondary mb-0 text-center">
                No se encontraron distribuidores con ese criterio.
              </div>
            </div>
          </div>
        </div>

        <!-- Formulario: ser distribuidor -->
        <div class="become-distributor p-4 p-lg-5">
          <div class="row align-items-center">
            <div class="col-lg-6 mb-4 mb-lg-0">
              <h2 class="h2 font-weight-bold text-white mb-3">¿Deseas ser nuestro distribuidor?</h2>
              <p class="lead text-white-70 mb-4">
                Únete a nuestra red de distribuidores autorizados y forma parte del futuro de la tecnología de drones en
                México.
              </p>

              <div class="mb-3 d-flex">
                <i class="fa-solid fa-building text-white mr-3 mt-1"></i>
                <div>
                  <h6 class="text-white font-weight-bold mb-1">Soporte Empresarial</h6>
                  <div class="text-white-70">Capacitación completa y soporte técnico continuo.</div>
                </div>
              </div>

              <div class="mb-3 d-flex">
                <i class="fa-solid fa-users text-white mr-3 mt-1"></i>
                <div>
                  <h6 class="text-white font-weight-bold mb-1">Red de Contactos</h6>
                  <div class="text-white-70">Acceso a una amplia red de clientes y oportunidades.</div>
                </div>
              </div>

              <div class="d-flex">
                <i class="fa-solid fa-earth-americas text-white mr-3 mt-1"></i>
                <div>
                  <h6 class="text-white font-weight-bold mb-1">Territorio Exclusivo</h6>
                  <div class="text-white-70">Derechos de distribución en tu región asignada.</div>
                </div>
              </div>
            </div>

            <div class="col-lg-6">
              <b-form @submit.prevent="handleSubmit" class="become-form">
                <div class="form-row">
                  <b-form-group label="Nombre Completo *" label-for="bd-name" label-class="text-white font-weight-bold" class="col-md-6 become-form-group">
                    <b-form-input
                      id="bd-name"
                      v-model.trim="formData.name"
                      type="text"
                      required
                      placeholder="Tu nombre completo"
                      class="become-input"
                    />
                  </b-form-group>

                  <b-form-group label="Correo Electrónico *" label-for="bd-email" label-class="text-white font-weight-bold" class="col-md-6 become-form-group">
                    <b-form-input
                      id="bd-email"
                      v-model.trim="formData.email"
                      type="email"
                      required
                      placeholder="tu@email.com"
                      class="become-input"
                    />
                  </b-form-group>
                </div>

                <div class="form-row">
                  <b-form-group label="Teléfono *" label-for="bd-phone" label-class="text-white font-weight-bold" class="col-md-6 become-form-group">
                    <b-form-input
                      id="bd-phone"
                      v-model.trim="formData.phone"
                      type="tel"
                      required
                      placeholder="+52 55 1234 5678"
                      class="become-input"
                    />
                  </b-form-group>

                  <b-form-group label="Empresa *" label-for="bd-company" label-class="text-white font-weight-bold" class="col-md-6 become-form-group">
                    <b-form-input
                      id="bd-company"
                      v-model.trim="formData.company"
                      type="text"
                      placeholder="Nombre de tu empresa"
                      class="become-input"
                      required
                    />
                  </b-form-group>
                </div>

                <b-form-group label="Ubicación *" label-for="bd-location" label-class="text-white font-weight-bold" class="become-form-group">
                  <b-form-input
                    id="bd-location"
                    v-model.trim="formData.location"
                    type="text"
                    required
                    placeholder="Ciudad, Estado"
                    class="become-input"
                  />
                </b-form-group>

                <b-form-group label="Experiencia en el Sector" label-for="bd-experience" label-class="text-white font-weight-bold" class="become-form-group">
                  <b-form-input
                    id="bd-experience"
                    v-model.trim="formData.experience"
                    type="text"
                    required
                    placeholder="Años de experiencia o sector relacionado *"
                    class="become-input"
                  />
                </b-form-group>

                <b-form-group label="Mensaje Adicional *" label-for="bd-message" label-class="text-white font-weight-bold" class="become-form-group">
                  <b-form-textarea
                    id="bd-message"
                    v-model.trim="formData.msg"
                    rows="4"
                    placeholder="Cuéntanos por qué quieres ser nuestro distribuidor..."
                    class="become-input"
                    required
                  />
                </b-form-group>

                <b-button type="submit" variant="light" block class="become-submit" :disabled="disabledButton">
                  Enviar Solicitud
                </b-button>
              </b-form>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      disabledButton: false,
      searchTerm: '',
      placeholderLogo: 'public/images/pages/distributors/placeholder.svg',
      distributors: [
        // {
        //   id: 1,
        //   name: 'TechDrones México',
        //   logo: 'public/images/pages/distributors/tech-drones-logo.jpg',
        //   description: 'Especialistas en drones para agricultura y mapeo en el centro de México.',
        //   location: 'Ciudad de México',
        //   phone: '+52 55 1234 5678',
        //   email: 'contacto@techdrones.mx',
        //   website: 'www.techdrones.mx',
        // },
      ],

      formData: {
        name: '',
        email: '',
        phone: '',
        company: '',
        location: '',
        experience: '',
        msg: '',
      },
    };
  },

  computed: {
    filteredDistributors() {
      const q = (this.searchTerm || '').toLowerCase();
      if (!q) return this.distributors;
      return this.distributors.filter((d) => {
        return (
          (d.name || '').toLowerCase().includes(q) ||
          (d.address || '').toLowerCase().includes(q)
        );
      });
    },
  },

  methods: {
    makeToast(variant = null, msg, title) {
      this.$bvToast.toast(msg, {
        title: title,
        variant: variant,
        solid: true,
        toaster: 'b-toaster-top-right',
        appendToast: true
      })
    },

    onlyNumbers(value) {
      let arr = value.split(',');
      for (let i = 0; i < arr.length; i++) {
        arr[i] = `<a class="text-muted small" href="tel:${arr[i].replace(/\D+/g, '')}">${arr[i].trim()}</a>`;
      }
      return arr.join('<span class="text-muted">, </span>');
    },

    normalizeWebsite(value) {
      const v = String(value || '').trim();
      if (!v) return '#';
      if (v.startsWith('http://') || v.startsWith('https://')) return v;
      return 'https://' + v;
    },

    handleSubmit() {
      this.disabledButton = true;
      
      axios.post(tools.url('/api/distributors-form'), this.formData).then((response) => {
        this.makeToast('success', response.data.response, 'Mensaje enviado');
        this.formData = {
          name: '',
          email: '',
          phone: '',
          company: '',
          location: '',
          experience: '',
          msg: '',
        };
        this.disabledButton = false;
      }).catch((error) => {
        // console.log(errors);
        let errors = error.response.data;
        this.makeToast('danger', error.response.data.name, 'Error');
        this.makeToast('danger', error.response.data.email, 'Error');
        this.makeToast('danger', error.response.data.phone, 'Error');
        this.makeToast('danger', error.response.data.company, 'Error');
        this.makeToast('danger', error.response.data.location, 'Error');
        this.makeToast('danger', error.response.data.experience, 'Error');
        this.makeToast('danger', error.response.data.msg, 'Error');
        this.disabledButton = false;
      });  
    },

    phoneNumber(phone) {
      let result = phone.replace(/\D/g, '');

      return 'tel:'+result;
    },

    getDistributors(){
      axios.get(tools.url('/api/distributors')).then((response)=>{
        this.distributors = response.data;
      }).catch((error)=>{
        console.log(error);
      });
    },
  },

  mounted(){
    this.getDistributors();
  }
};
</script>